<template>
	<div v-if="currentQuote.text" class="wisdom-widget" :class="{ 'wisdom-widget--visible': visible }">
		<div class="wisdom-widget__quote-mark">"</div>
		<Transition name="quote-fade" mode="out-in">
			<div :key="currentQuote.text" class="wisdom-widget__content">
				<p class="wisdom-widget__text">{{ currentQuote.text }}</p>
				<span class="wisdom-widget__author">&mdash; {{ currentQuote.author }}</span>
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import wisdomQuoteService, { type WisdomQuote } from "@/services/wisdomQuoteService";

const { locale } = useI18n();

const visible = ref(false);
const currentQuote = ref<WisdomQuote>({ text: "", author: "" });

let interval: ReturnType<typeof setInterval> | null = null;

async function fetchQuote() {
	try {
		const quote = await wisdomQuoteService.getRandomQuote(locale.value);
		if (quote?.text) {
			currentQuote.value = quote;
		}
	} catch {
		// silently keep current quote on error
	}
}

onMounted(async () => {
	await fetchQuote();
	requestAnimationFrame(() => {
		visible.value = true;
	});
	interval = setInterval(fetchQuote, 300000);
});

onUnmounted(() => {
	if (interval) clearInterval(interval);
});
</script>

<style lang="scss" scoped>
@use "@/styles/_variables.scss" as vars;

.wisdom-widget {
	position: relative;
	padding: 0.875rem 1.5rem 0.875rem 2.25rem;
	border-radius: 10px;
	background: rgba(var(--v-theme-primary), 0.04);
	border-left: 3px solid rgba(var(--v-theme-primary), 0.25);
	opacity: 0;
	transform: translateY(8px);
	transition: opacity 0.8s ease, transform 0.8s ease;

	&--visible {
		opacity: 1;
		transform: translateY(0);
	}

	&__quote-mark {
		position: absolute;
		top: 0rem;
		left: 0.5rem;
		font-family: "Cormorant Garamond", "Georgia", serif;
		font-size: 2.5rem;
		font-weight: 700;
		line-height: 1;
		color: rgb(var(--v-theme-primary));
		opacity: 0.25;
		pointer-events: none;
		user-select: none;
	}

	&__content {
		display: flex;
		flex-direction: column;
		gap: 0.375rem;
	}

	&__text {
		font-family: "Cormorant Garamond", "Georgia", serif;
		font-size: 1.15rem;
		font-weight: 500;
		font-style: italic;
		line-height: 1.5;
		color: rgb(var(--v-theme-on-background));
		margin: 0;
	}

	&__author {
		font-family: vars.$font-secondary;
		font-size: 0.8rem;
		font-weight: 600;
		letter-spacing: 0.04em;
		color: rgb(var(--v-theme-primary));
		opacity: 0.7;
	}
}

// Transição entre frases
.quote-fade-enter-active,
.quote-fade-leave-active {
	transition: opacity 0.5s ease, transform 0.5s ease;
}

.quote-fade-enter-from {
	opacity: 0;
	transform: translateY(6px);
}

.quote-fade-leave-to {
	opacity: 0;
	transform: translateY(-6px);
}

@media (max-width: 960px) {
	.wisdom-widget {
		&__text {
			font-size: 0.9rem;
		}
	}
}
</style>
